import type { PayloadAction } from "@reduxjs/toolkit";
import { createSelector, createSlice } from "@reduxjs/toolkit";

import { AppState } from "./store";

/**
 * Which slice of the projects list is on screen. "completed" is derived — every
 * milestone ticked off — while "archived" is the flag a user sets by hand, so a
 * project lands in Completed on its own and only leaves once someone files it.
 */
export type ProjectsTab = "active" | "completed" | "archived";

export interface FilmsState {
  selectedProjectID?: number | undefined;
  selectedBenefitID?: number | undefined;
  selectedTaskTypeID?: number | undefined;
  selectedJobID?: number | undefined;
  selectedScaleID?: number | undefined;
  selectedTalentID?: number | undefined;
  selectedVendorID?: number | undefined;
  selectedCueID?: number | undefined;
  selectedProjectTypeID?: number | undefined;
  selectedPositionID?: number | undefined;
  selectedJobCategoryID?: number | undefined;
  selectedVtransactionTalentID?: number | undefined;
  projectsTab?: ProjectsTab;
  showArchivedTalents?: boolean;
  /** Narrow the main ledger to starred talents — the core roster. */
  ledgerFavoritesOnly?: boolean;
  /**
   * Shared by the Rates grid and the Project Types / Task Types admin grids:
   * they are three views onto the same archived rates, so revealing them in
   * one is meant to reveal them in all three.
   */
  showArchivedRates?: boolean;
  tabIndexes: { [key: string]: number };
  formOpen: { [key: string]: boolean };
}

const initialState = {
  tabIndexes: {},
  formOpen: {},
} as FilmsState;

export const filmsSlice = createSlice({
  name: "films",
  initialState,
  reducers: {
    setFormOpen(
      state,
      action: PayloadAction<{ formID: FormID; isOpen: boolean }>
    ) {
      const { formID, isOpen } = action.payload;
      state.formOpen[formID] = isOpen;
    },
    setSelectedTaskTypeID(state, action: PayloadAction<number | undefined>) {
      state.selectedTaskTypeID = action.payload;
    },
    setSelectedVendorID(state, action: PayloadAction<number | undefined>) {
      state.selectedVendorID = action.payload;
    },
    setSelectedProjectID(state, action: PayloadAction<number | undefined>) {
      state.selectedProjectID = action.payload;
    },
    setSelectedTalentID(state, action: PayloadAction<number | undefined>) {
      state.selectedTalentID = action.payload;
    },
    setSelectedScaleID(state, action: PayloadAction<number | undefined>) {
      state.selectedScaleID = action.payload;
    },
    setSelectedBenefitID(state, action: PayloadAction<number | undefined>) {
      state.selectedBenefitID = action.payload;
    },
    setSelectedCueID(state, action: PayloadAction<number | undefined>) {
      state.selectedCueID = action.payload;
    },
    setSelectedProjectTypeID(state, action: PayloadAction<number | undefined>) {
      state.selectedProjectTypeID = action.payload;
    },
    setSelectedJobID(state, action: PayloadAction<number | undefined>) {
      state.selectedJobID = action.payload;
    },
    setSelectedJobCategoryID(state, action: PayloadAction<number | undefined>) {
      state.selectedJobCategoryID = action.payload;
    },
    setProjectsTab(state, action: PayloadAction<ProjectsTab>) {
      state.projectsTab = action.payload;
    },
    setShowArchivedTalents(state, action: PayloadAction<boolean>) {
      state.showArchivedTalents = action.payload;
    },
    setLedgerFavoritesOnly(state, action: PayloadAction<boolean>) {
      state.ledgerFavoritesOnly = action.payload;
    },
    setShowArchivedRates(state, action: PayloadAction<boolean>) {
      state.showArchivedRates = action.payload;
    },
    setSelectedPositionID(state, action: PayloadAction<number | undefined>) {
      state.selectedPositionID = action.payload;
    },
    setSelectedVtransactionTalentID(
      state,
      action: PayloadAction<number | undefined>
    ) {
      state.selectedVtransactionTalentID = action.payload;
    },
    setTabIndex(state, action: PayloadAction<{ tabID: TabID; value: number }>) {
      const { tabID, value } = action.payload;
      state.tabIndexes[tabID] = value;
    },
  },
});

export const {
  setFormOpen,
  setSelectedVendorID,
  setTabIndex,
  setSelectedTaskTypeID,
  setSelectedTalentID,
  setShowArchivedTalents,
  setLedgerFavoritesOnly,
  setShowArchivedRates,
  setSelectedBenefitID,
  setSelectedProjectID,
  setSelectedScaleID,
  setProjectsTab,
  setSelectedProjectTypeID,
  setSelectedVtransactionTalentID,
  setSelectedJobCategoryID,
  setSelectedPositionID,
  setSelectedJobID,
  setSelectedCueID,
} = filmsSlice.actions;

export type FormID =
  | "benefit"
  | "budget"
  | "cue"
  | "expense"
  | "job"
  | "jobCategory"
  | "jobTask"
  | "labor"
  | "musicPrep"
  | "orchestration"
  | "position"
  | "project"
  | "projectCue"
  | "projectTask"
  | "projectType"
  | "rate"
  | "rateScale"
  | "reconciliation"
  | "scale"
  | "talent"
  | "taskType"
  | "vendor"
  | "vTransaction";

type TabID = "customer";

export const formOpenSelector = (formID: FormID) => {
  return createSelector([(s) => s.films.formOpen], (s) => {
    return s[formID] ?? false;
  });
};

export const formOpenMapSelector = createSelector(
  [(s) => s.films.formOpen],
  (s) => s
);

export const tabIndexSelector = (tabID: TabID) => {
  return createSelector([(s) => s.films.tabIndexes], (s) => {
    return s[tabID] ?? 0;
  });
};

export const selectedProjectID = createSelector(
  (s: AppState) => s.films.selectedProjectID,
  (s) => s
);

export const selectedTaskTypeID = createSelector(
  (s: AppState) => s.films.selectedTaskTypeID,
  (s) => s
);

export const selectedVendorID = createSelector(
  (s: AppState) => s.films.selectedVendorID,
  (s) => s
);

export const selectedBenefitID = createSelector(
  (s: AppState) => s.films.selectedBenefitID,
  (s) => s
);

export const selectedCueID = createSelector(
  (s: AppState) => s.films.selectedCueID,
  (s) => s
);

export const selectedJobID = createSelector(
  (s: AppState) => s.films.selectedJobID,
  (s) => s
);

export const selectedTalentID = createSelector(
  (s: AppState) => s.films.selectedTalentID,
  (s) => s
);

export const selectedVtransactionTalentID = createSelector(
  (s: AppState) => s.films.selectedVtransactionTalentID,
  (s) => s
);

export const selectedJobCategoryID = createSelector(
  (s: AppState) => s.films.selectedJobCategoryID,
  (s) => s
);

export const selectedPositionID = createSelector(
  (s: AppState) => s.films.selectedPositionID,
  (s) => s
);

export const selectedScaleID = createSelector(
  (s: AppState) => s.films.selectedScaleID,
  (s) => s
);

export const selectedProjectTypeID = createSelector(
  (s: AppState) => s.films.selectedProjectTypeID,
  (s) => s
);

export const showArchivedTalents = createSelector(
  (s: AppState) => s.films.showArchivedTalents,
  (s) => s
);

export const showArchivedRates = createSelector(
  (s: AppState) => s.films.showArchivedRates ?? false,
  (s) => s
);

export const ledgerFavoritesOnly = createSelector(
  (s: AppState) => s.films.ledgerFavoritesOnly ?? false,
  (s) => s
);

export const projectsTab = createSelector(
  (s: AppState) => s.films.projectsTab ?? "active",
  (s) => s
);
